import { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MENU_SEARCH_MIN, searchMenuItems, type MenuKind } from '@/lib/menu-items';

interface Props {
  value: string;
  onChange: (name: string) => void;
  /** Categoria do cardápio priorizada na busca. */
  kind: MenuKind;
  placeholder?: string;
  /** Adorno à esquerda (ex: "2X" nas dobradas). Default: lupa. */
  leading?: React.ReactNode;
  /** Colagem de várias linhas — se tratar, o paste normal é cancelado. */
  onPasteList?: (text: string) => boolean;
  className?: string;
}

export function MenuItemAutocomplete({
  value,
  onChange,
  kind,
  placeholder,
  leading,
  onPasteList,
  className,
}: Props) {
  const [focused, setFocused] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const results = focused && query.trim().length >= MENU_SEARCH_MIN ? searchMenuItems(query, kind) : [];
  const open = results.length > 0;

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setFocused(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div ref={rootRef} className={cn('relative w-full', className)}>
      <div className="relative">
        <input
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setQuery(e.target.value);
          }}
          onFocus={() => {
            setQuery(value);
            setFocused(true);
          }}
          onPaste={(e) => {
            if (!onPasteList) return;
            const texto = e.clipboardData.getData('text');
            if (!texto.includes('\n')) return;
            if (onPasteList(texto)) {
              e.preventDefault();
              setFocused(false);
            }
          }}
          placeholder={placeholder}
          className={cn(
            'w-full rounded-lg border border-border bg-card py-3 pr-3 font-mono text-[12px] uppercase tabular-nums text-foreground outline-none transition-colors focus:border-foreground',
            leading ? 'pl-10' : 'pl-9',
          )}
        />
        {leading ?? (
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        )}
      </div>

      {open && (
        <div className="absolute left-0 right-0 z-40 mt-1 max-h-56 overflow-hidden overflow-y-auto rounded-lg border border-border bg-card shadow-lg">
          {results.map((it) => (
            <button
              key={`${it.group}-${it.name}`}
              type="button"
              onClick={() => {
                onChange(it.name);
                setFocused(false);
              }}
              className="mono flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[12px] uppercase tabular-nums tracking-wider hover:bg-muted"
            >
              <span className="truncate font-bold text-foreground">{it.name}</span>
              <span className="shrink-0 text-muted-foreground">{it.group}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
